/**
 * API service layer for hub-ui
 * Provides centralized methods for making API calls to the hub server
 */

import { getRuntimeConfig, isElectron } from '../config/runtime';
import { getDirectHubOrigin } from '../config/runtime';
import { useAuthStore } from '../stores/auth';
import { isDeviceReachableEndpoint } from './deviceCredential';
import { selectRequestCredential, type PanelScope } from './apiCredential';
import { resolveWindowPanelScope } from './panelScopes';
import type { ApiTransportOptions } from './apiTransport';
import { routedFetch } from './apiRouter';
import { isDirectHubUrl } from './hubRequestRouting';
import { sanitizeHubRequestHeaders } from './hubCorsHeaders';
import { markHubResponse } from '../utils/bootTiming';
import { field, wireString } from '../utils/wire';
import type { ElectronHttpAnswer } from './hubFetch';
import type { DeviceClass, DeviceClassMetadata } from '../stores/deviceClasses';

export interface ApiFetchOptions extends RequestInit, ApiTransportOptions {
  /** Bearer token; `null` is accepted so callers can pass a store value directly. */
  authToken?: string | null;
  /**
   * The panel scope this request belongs to. A panel surface that is NOT the
   * window (the miniapp) declares its own scope here, so its credential is used
   * for its calls and for nothing else — the rest of the app keeps the user
   * session, and a background poll can never be answered with a panel token.
   */
  panelScope?: PanelScope | null;
  baseUrl?: string;
  bypassRouting?: boolean;
  onUploadProgress?: (percent: number) => void;
  /** Never attempt the silent local-hub token refresh + retry on 401. */
  skipAuthRefresh?: boolean;
}

/**
 * The budget for a request that deletes a device.
 *
 * The hub deletes a device's history with it — one demo device on the dev hub
 * carried 2.3M `value_logs` rows — and answers only once the row is gone. That
 * is tens of seconds, well past every transport's default request budget, so
 * every device-delete call site passes this and the window stays honest about
 * what it is waiting for.
 */
export const DEVICE_DELETE_TIMEOUT_MS = 3 * 60 * 1000;

type ApiError = Error & {
  status?: number;
  statusText?: string;
  /** The failure body the hub answered with — untyped JSON; read it with a check. */
  data?: unknown;
};

function createApiError(message: string, response: Response, data?: unknown): ApiError {
  const error = new Error(message) as ApiError;
  error.status = response.status;
  error.statusText = response.statusText;
  error.data = data;
  return error;
}

async function readJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    const snippet = text.trim().slice(0, 200).toLowerCase();
    if (snippet.startsWith('<!doctype') || snippet.startsWith('<html')) {
      const responseUrl = response.url || 'unknown URL';
      console.error('[API] Received HTML instead of JSON', {
        url: responseUrl,
        status: response.status,
        htmlPreview: text.trim().slice(0, 500),
      });
      throw new Error(
        `Received HTML instead of JSON from API (${responseUrl}). ` +
          `Status: ${response.status}. Check selected hub IP/port and API routing.`,
      );
    }
    throw new Error('Received invalid JSON from API response.');
  }
}

function buildElectronProxyResponse(result: ElectronHttpAnswer): Response {
  let body: BodyInit | null = null;

  if (result.bodyEncoding === 'base64' && result.body) {
    const binary = atob(result.body);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    body = bytes;
  } else if (typeof result.body === 'string') {
    body = result.body;
  }

  return new Response(body, {
    status: result.status,
    statusText: result.statusText,
    headers: new Headers(result.headers || {}),
  });
}

async function electronProxyFetch(url: string, options: RequestInit): Promise<Response> {
  const electron = window.electron;
  if (options.body instanceof FormData) {
    // The otto-local protocol handler can receive real multipart/form-data
    // requests directly, while the Electron IPC bridge would stringify the
    // FormData and drop uploaded files.
    return fetch(url, options);
  }
  if (!electron?.httpRequest) {
    return fetch(url, options);
  }

  const headers = (options.headers as Record<string, string>) || {};
  const rawBody =
    typeof options.body === 'string' ? options.body : options.body ? JSON.stringify(options.body) : undefined;

  const method = options.method || 'GET';
  const result = await electron.httpRequest({
    url,
    method,
    headers,
    body: rawBody,
  });

  markHubResponse(url, result.status, 'ipc');

  console.log('[ElectronProxy] IPC result', {
    url,
    method,
    status: result.status,
    statusText: result.statusText,
    bodyType: typeof result.body,
    bodyPreview: typeof result.body === 'string' ? result.body.slice(0, 200) : result.body,
    dataType: typeof result.data,
    isArray: Array.isArray(result.data),
  });

  if (result.status === 0) {
    throw new Error(result.statusText || 'Electron request failed');
  }

  return buildElectronProxyResponse(result);
}

/**
 * Base fetch wrapper with authentication and routing
 * Returns the raw Response object for flexible response handling
 */
export async function apiFetchRaw(endpoint: string, options: ApiFetchOptions = {}): Promise<Response> {
  const config = getRuntimeConfig();
  const authStore = useAuthStore();

  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  // Only set Content-Type to application/json for requests with a body
  // GET/HEAD requests with Content-Type set can confuse some servers
  const method = ((options.method || 'GET') as string).toUpperCase();
  if (!(options.body instanceof FormData) && method !== 'GET' && method !== 'HEAD') {
    headers['Content-Type'] = 'application/json';
  }

  // ─── Which token goes out (three shapes, one rule) ─────────────────────────
  //
  // The rule is `selectRequestCredential()` in `services/apiCredential.ts` —
  // pure, unit-tested, and the single answer to "who is asking". Summarised:
  //
  //   1. an explicit `authToken` (a caller that owns its credential);
  //   2. the PANEL credential, for a request that belongs to a panel scope
  //      (`options.panelScope`, or the window itself being a configured panel)
  //      and ONLY on the route families the hub accepts it on;
  //   3. the user JWT of this hub session — and never the panel credential, so a
  //      stale `touch_device_token` (or a stale `touch_panel_mode=true` left by an
  //      interrupted pairing) cannot hijack a user route the way it once did;
  //   4. the ENROLLED DEVICE CREDENTIAL (F5), last and narrow: only on the route
  //      families the hub accepts one (`isDeviceReachableEndpoint`) and only when
  //      no user JWT answers for this session.
  //
  // The window's panel scope comes from an EXPLICIT device configuration
  // (`config/panelDevice.ts`), never from the hardware it is running on.
  const windowScope = resolveWindowPanelScope();
  const ownerScope = options.panelScope ?? null;
  const panelCredentialAvailable = Boolean((ownerScope ?? windowScope)?.credential);

  // The device credential is the last shape: rotate it before it expires rather
  // than after a 401 says so, but only look for it when nothing above can answer.
  // This call WANTS a successor, so it stays on the renewal (`POST`,
  // `refreshDeviceCredential`) — the 401 verdict below is the one caller that
  // must not spend what it is asking about.
  //
  // "The user JWT" here is `connectionUserToken()`, NOT `authStore.token`: a token
  // this window cannot attribute to the connection in use (it was read from the
  // unscoped `accessToken` key, or written for the hub this window was on before
  // it moved) is not evidence for this hub — the hub answers it 401 and the app
  // used to turn that into "Your session has expired. Please sign in again."
  // while the credential the hub DOES accept sat unread on the same device
  // (card `t_68d110ee`).
  const userToken = authStore.connectionUserToken();
  let deviceCredential: string | null = null;
  if (!options.authToken && !userToken && !panelCredentialAvailable && isDeviceReachableEndpoint(endpoint)) {
    deviceCredential = authStore.resolveDeviceCredential()?.token ?? null;
    if (deviceCredential && authStore.deviceCredentialNeedsRefresh()) {
      await authStore.refreshDeviceCredential();
      deviceCredential = authStore.resolveDeviceCredential()?.token ?? null;
    }
  }

  const decision = selectRequestCredential({
    endpoint,
    callerToken: options.authToken,
    ownerScope,
    windowScope,
    userToken,
    deviceCredential,
  });

  const token = decision.token;
  const panelRuntime = decision.shape === 'panel';
  const deviceCredentialPresented = decision.shape === 'device';

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    if (decision.shape === 'panel') {
      console.log('[API] Using panel credential', {
        endpoint,
        scope: (ownerScope ?? windowScope)?.id,
        reason: decision.reason,
      });
    } else if (deviceCredentialPresented) {
      console.log('[API] Using enrolled device credential', { endpoint });
    }
  } else if (decision.panelWithheld) {
    // The case the old `panelRuntime` guard existed to prevent: a panel
    // credential was present and this route is not one that accepts it.
    console.warn('[API] Panel credential withheld', { endpoint, reason: decision.reason });
  }

  const baseUrl = options.baseUrl ?? config.apiUrl;
  let requestUrl = /^([a-z][a-z\d+.-]*:)?\/\//i.test(endpoint) ? endpoint : `${baseUrl}${endpoint}`;

  // Safety: strip otto-local:// protocol when NOT in Electron mode.
  // This can happen when localStorage retains a hub IP from a previous
  // Electron session and the browser resolves otto-local:// incorrectly.
  if (!isElectron() && requestUrl.startsWith('otto-local://')) {
    const safeUrl = requestUrl.replace(/^otto-local:\/\//, 'http://');
    console.warn('[API] Stripped otto-local:// protocol (non-Electron mode)', {
      original: requestUrl,
      normalized: safeUrl,
    });
    requestUrl = safeUrl;
  }

  // Diagnostic: warn if apiUrl is relative (may hit wrong origin)
  if (baseUrl.startsWith('/')) {
    console.warn('[API] Relative apiUrl detected — requests will target current origin', {
      apiUrl: baseUrl,
      endpoint,
      requestUrl,
      origin: window.location.origin,
      mode: config.mode,
    });
  }

  console.log('[API] Fetch', { method, requestUrl });

  // A hub-answered request carries only the headers the hub names in its CORS
  // allow-list: the browser refuses anything else at preflight, so the request
  // never leaves this window and the hub logs nothing (card `t_68d110ee`). Drop
  // and report, rather than send a request that cannot arrive.
  if (isDirectHubUrl(requestUrl)) {
    const { headers: allowedHeaders, refused } = sanitizeHubRequestHeaders(headers);
    if (refused.length > 0) {
      console.warn('[API] Dropped headers this hub does not allow (they would fail CORS preflight)', {
        requestUrl,
        refused,
      });
      for (const key of Object.keys(headers)) {
        if (!(key in allowedHeaders)) delete headers[key];
      }
    }
  }

  const requestInit: ApiFetchOptions = {
    ...options,
    headers,
  };

  // Route normal traffic through routedFetch so Electron cloud sessions can
  // prefer WebRTC even if a saved local otto-local API URL is still present.
  // Direct bridge bypass is reserved for explicit control-plane callers.
  //
  // 401 handling: when the local hub token expired, attempt ONE silent refresh
  // (sliding session or saved-credentials re-login) and retry the request.
  // Panel requests use device tokens (own refresh flow) and requests with an
  // explicit authToken are skipped, as are endpoints marked skipAuthRefresh.
  const canAuthRefresh = !options.skipAuthRefresh && !panelRuntime && !options.authToken && !!userToken;
  const isGet = method === 'GET';
  let maxAttempts = isGet ? 2 : 1;
  let authRetried = false;
  let deviceCredentialProbed = false;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const fetchFn = options.bypassRouting ? (isElectron() ? electronProxyFetch : fetch) : routedFetch;
      const response = await fetchFn(requestUrl, requestInit);

      if (!response.ok) {
        let errorData: unknown = {};
        try {
          errorData = await readJsonSafe(response);
        } catch (__caught: unknown) {
          const parseError = __caught as Error;
          if (isGet && attempt < maxAttempts - 1 && parseError?.message?.includes('Received HTML')) {
            console.warn(`[API] Retry ${attempt + 1}/${maxAttempts - 1} after HTML response for ${requestUrl}`);
            await new Promise((r) => {
              setTimeout(r, 1000);
            });
            continue;
          }
          throw createApiError(parseError?.message || `API error: ${response.status} ${response.statusText}`, response);
        }

        // Expired/invalid local hub token: try one silent refresh + retry.
        if (response.status === 401 && canAuthRefresh && !authRetried) {
          authRetried = true;
          console.warn('[API] 401 received, attempting local hub token refresh + retry', {
            requestUrl,
          });
          const refreshed = await authStore.refreshLocalHubToken();
          const renewed = authStore.connectionUserToken();
          if (refreshed && renewed) {
            headers['Authorization'] = `Bearer ${renewed}`;
            requestInit.headers = headers;
            maxAttempts = Math.max(maxAttempts, attempt + 2);
            continue;
          }
          // The refresh could not renew the session. Before calling the session
          // dead: if the token this window presented has turned out NOT to be
          // this connection's (it slid in from another hub — the app switched
          // hubs while it was held), drop it and retry once. The connection's
          // own credential — the enrolled device credential this hub accepts —
          // then answers, instead of a session being torn down over a token
          // that was never this hub's (card `t_68d110ee`).
          if (authStore.dropUnattributableLocalToken()) {
            console.warn(
              "[API] Dropped a local hub token that was not this connection's; retrying with the connection's own credential",
              {
                requestUrl,
              },
            );
            delete headers['Authorization'];
            requestInit.headers = headers;
            maxAttempts = Math.max(maxAttempts, attempt + 2);
            continue;
          }
          // Refresh impossible without user interaction — notify once, then
          // surface the original 401 to the caller.
          await authStore.notifySessionExpired();
        }

        // A 401 on a request that carried the ENROLLED DEVICE CREDENTIAL.
        // Only the credential's own route says `invalid_device_token`; everywhere
        // else a rejected credential looks like any other 401, and a route that
        // simply wants a user JWT would answer the same. So ask the credential's
        // own route — with the verb that CANNOT spend it (`GET`, card
        // `t_7aa9066f`): a `200` means the credential is good and this 401 was
        // about something else, and nothing is rotated to find that out. On a hub
        // without the probe route the question falls back to the renewal inside
        // the store. A genuine refusal clears the credential and says so once on
        // the sign-in screen (`stores/auth.ts` → `notifyDeviceCredentialRejected`),
        // with no retry of the refused token, so it cannot loop.
        if (response.status === 401 && deviceCredentialPresented && !deviceCredentialProbed) {
          deviceCredentialProbed = true;
          console.warn('[API] 401 on an enrolled-device request, asking the hub about the credential', {
            requestUrl,
            code: field(errorData, 'code'),
          });
          const verdict = await authStore.probeDeviceCredential();
          if (verdict === 'invalid') {
            console.warn('[API] Device credential refused — dropped, the device has to enroll again');
          }
        }

        const serverMessage = wireString(field(errorData, 'error')) || wireString(field(errorData, 'message'));
        throw createApiError(
          serverMessage || `API error: ${response.status} ${response.statusText}`,
          response,
          errorData,
        );
      }

      return response;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      lastError = err;
      const isNetworkError =
        err instanceof TypeError || err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError');
      const isHtmlError = err.message?.includes('Received HTML');

      if (isGet && attempt < maxAttempts - 1 && (isNetworkError || isHtmlError)) {
        console.warn(`[API] Retry ${attempt + 1}/${maxAttempts - 1} for ${requestUrl}: ${err.message}`);
        await new Promise((r) => {
          setTimeout(r, 1000);
        });
        continue;
      }
      throw err;
    }
  }

  throw lastError;
}

/**
 * Base fetch wrapper with authentication and routing.
 * Returns parsed JSON data for backward compatibility.
 *
 * `T` is what the caller knows the endpoint answers. WITHOUT one the body is
 * untyped JSON, and that is the one `any` this migration leaves standing: 800
 * call sites read their own view of the body (a store's interface, an
 * `Array.isArray` guard, an inline shape), so the default cannot be narrowed
 * without typing every one of them — measured at 470 errors across ~60 files when
 * tried (see the batch-5 report, card t_ae99b52e, and the residue note in
 * eslint.config.mjs). Everything else in `src/services` is typed.
 */
export async function apiFetch<T = any>(endpoint: string, options: ApiFetchOptions = {}): Promise<T> {
  const response = await apiFetchRaw(endpoint, options);
  return readJsonSafe(response) as Promise<T>;
}

/**
 * Returns the base URL for inbound webhook endpoints.
 * e.g. "http://192.168.1.x:3000/webhooks"
 */
export function getWebhookBaseUrl(): string {
  getRuntimeConfig();
  return getDirectHubOrigin() + '/webhooks';
}

/**
 * Regenerate the hookId for a webhook trigger.
 * Returns the new hookId and the full webhook URL.
 */
export async function regenerateWebhookHook(triggerId: string): Promise<{ hookId: string; webhookUrl: string }> {
  return apiFetch(`/routines/triggers/${triggerId}/regenerate-hook`, { method: 'POST' });
}

/**
 * The verdict `POST /device-classes/:id/validate-settings` returns. The hub sends
 * more; the class editor reads these two, so they are the ones named here.
 */
export interface DeviceClassValidationResult {
  valid: boolean;
  errors?: string[];
  [key: string]: unknown;
}

/**
 * Device Classes API
 *
 * The rows are the hub's own JSON: `DeviceClassMetadata`/`DeviceClass` are the
 * device-class store's view of them (imported here rather than re-declared, so
 * the store and the wire cannot drift apart silently).
 */
export const deviceClassesApi = {
  /**
   * Get all device classes, optionally filtered
   */
  async getAll(integration?: string, category?: string): Promise<DeviceClassMetadata[]> {
    const params = new URLSearchParams();
    if (integration) params.append('integration', integration);
    if (category) params.append('category', category);
    const query = params.toString();
    return apiFetch(`/device-classes${query ? '?' + query : ''}`);
  },

  /**
   * Get a specific device class with full details
   */
  async getById(classId: string, settings?: Record<string, unknown>): Promise<DeviceClass> {
    let url = `/device-classes/${classId}`;
    if (settings && Object.keys(settings).length > 0) {
      url += `?settings=${encodeURIComponent(JSON.stringify(settings))}`;
    }
    return apiFetch(url);
  },

  /**
   * Validate settings for a device class
   */
  async validateSettings(classId: string, settings: Record<string, unknown>): Promise<DeviceClassValidationResult> {
    return apiFetch(`/device-classes/${classId}/validate-settings`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  },

  /**
   * Get by integration type
   */
  async getByIntegration(integration: string): Promise<DeviceClassMetadata[]> {
    return this.getAll(integration);
  },

  /**
   * Get by category
   */
  async getByCategory(category: string): Promise<DeviceClassMetadata[]> {
    return this.getAll(undefined, category);
  },
};

/**
 * Extended request options
 */
interface ApiRequestOptions extends RequestInit, ApiTransportOptions {
  params?: Record<string, string | number | boolean>;
  responseType?: 'json' | 'blob' | 'text';
}

/**
 * Default API object with standard HTTP methods
 */
const api = {
  async get(endpoint: string, options: ApiRequestOptions = {}) {
    const { params, responseType = 'json', ...fetchOptions } = options;

    // Add query parameters to endpoint
    let url = endpoint;
    if (params) {
      const queryString = new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)]),
      ).toString();
      url = `${endpoint}?${queryString}`;
    }

    const response = await apiFetchRaw(url, { ...fetchOptions, method: 'GET' });

    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob };
    } else if (responseType === 'text') {
      return response.text();
    }
    return response.json();
  },

  async post(endpoint: string, data?: unknown, options: ApiRequestOptions = {}) {
    const { responseType = 'json', ...fetchOptions } = options;
    const response = await apiFetchRaw(endpoint, {
      ...fetchOptions,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });

    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob };
    } else if (responseType === 'text') {
      return response.text();
    }
    return response.json();
  },

  async put(endpoint: string, data?: unknown, options: ApiRequestOptions = {}) {
    const { responseType = 'json', ...fetchOptions } = options;
    const response = await apiFetchRaw(endpoint, {
      ...fetchOptions,
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });

    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob };
    } else if (responseType === 'text') {
      return response.text();
    }
    return response.json();
  },

  async delete(endpoint: string, options: ApiRequestOptions = {}) {
    const { responseType = 'json', ...fetchOptions } = options;
    const response = await apiFetchRaw(endpoint, { ...fetchOptions, method: 'DELETE' });

    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob };
    } else if (responseType === 'text') {
      return response.text();
    }
    return response.json();
  },

  async patch(endpoint: string, data?: unknown, options: ApiRequestOptions = {}) {
    const { responseType = 'json', ...fetchOptions } = options;
    const response = await apiFetchRaw(endpoint, {
      ...fetchOptions,
      method: 'PATCH',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });

    if (responseType === 'blob') {
      const blob = await response.blob();
      return { data: blob };
    } else if (responseType === 'text') {
      return response.text();
    }
    return response.json();
  },
};

/**
 * Notifications API
 */
export const notificationsApi = {
  /**
   * Get user's notifications
   */
  async getAll(params?: { limit?: number; isRead?: boolean; type?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.isRead !== undefined) queryParams.append('isRead', params.isRead.toString());
    if (params?.type) queryParams.append('type', params.type);

    const query = queryParams.toString();
    return apiFetch(`/notifications${query ? `?${query}` : ''}`);
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    return apiFetch('/notifications/unread-count');
  },

  /**
   * Get single notification by ID
   */
  async getById(id: string) {
    return apiFetch(`/notifications/${id}`);
  },

  /**
   * Mark notification as read
   */
  async markAsRead(id: string) {
    return apiFetch(`/notifications/${id}/read`, { method: 'PUT' });
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    return apiFetch('/notifications/read-all', { method: 'PUT' });
  },

  /**
   * Delete notification
   */
  async delete(id: string) {
    return apiFetch(`/notifications/${id}`, { method: 'DELETE' });
  },

  /**
   * Delete all user notifications
   */
  async deleteAll() {
    return apiFetch('/notifications', { method: 'DELETE' });
  },

  /**
   * Get notification preferences
   */
  async getPreferences() {
    return apiFetch('/notifications/preferences');
  },

  /**
   * Update notification preferences
   */
  async updatePreferences(preferences: {
    notificationType: string;
    pushEnabled?: boolean;
    inAppEnabled?: boolean;
    emailEnabled?: boolean;
  }) {
    return apiFetch('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  },
};

export default api;
